import { memo } from 'react';
import { LAYOUT } from './layout';

export type BreakerState = 'closed' | 'open' | 'tripped' | null;
export type FlowDirection = 'towardBus' | 'awayFromBus' | 'none';

interface BranchLineProps {
  startX: number;     // card-side X
  endX: number;       // bus-side X
  y: number;          // row center Y
  breakerState: BreakerState;
  flowDirection: FlowDirection;
  color: string;
  side: 'left' | 'right';
}

function BranchLineInner({ startX, endX, y, breakerState, flowDirection, color, side }: BranchLineProps) {
  const isOpen = breakerState === 'open' || breakerState === 'tripped';
  const isClosed = breakerState === 'closed';
  const isTripped = breakerState === 'tripped';

  const lineColor = isOpen ? '#475569' : color;
  const lineOpacity = isOpen ? 0.5 : 0.8;
  const br = LAYOUT.breakerRadius;

  // Breaker position: centered on the branch line
  const lineLen = Math.abs(endX - startX);
  const breakerOffset = lineLen * 0.5;
  const breakerX = side === 'left'
    ? startX + breakerOffset
    : startX - breakerOffset;

  // Flow dot positions: compute start and travel distance in SVG coordinates
  // towardBus = dots flow from card toward bus
  // awayFromBus = dots flow from bus toward card (e.g. BESS charging)
  const showDots = isClosed && flowDirection !== 'none';

  let dotOriginX = 0;
  let dotTravelPx = 0;
  if (showDots) {
    if (flowDirection === 'towardBus') {
      // Card → Bus
      dotOriginX = startX;
      dotTravelPx = endX - startX; // sign handles direction automatically
    } else {
      // Bus → Card
      dotOriginX = endX;
      dotTravelPx = startX - endX;
    }
  }

  return (
    <g>
      {/* Main line */}
      <line
        x1={startX} y1={y} x2={endX} y2={y}
        stroke={lineColor} strokeWidth={2.5}
        opacity={lineOpacity}
        strokeLinecap="round"
        strokeDasharray={isOpen ? '6,4' : undefined}
      />

      {/* Closed breaker: solid colored dot on branch + dot at bus junction */}
      {isClosed && (
        <>
          <circle cx={breakerX} cy={y} r={br} fill={color} />
          <circle cx={endX} cy={y} r={br} fill={color} />
        </>
      )}

      {/* Tripped breaker: pulsing red */}
      {isTripped && (
        <circle cx={breakerX} cy={y} r={br} fill="#fb7185" className="sld-pulse" />
      )}

      {/* Open breaker: two hollow circles with diagonal slash */}
      {isOpen && (
        <g>
          <circle cx={breakerX - 7} cy={y} r={3.5} fill="none" stroke="#475569" strokeWidth={1.5} />
          <circle cx={breakerX + 7} cy={y} r={3.5} fill="none" stroke="#475569" strokeWidth={1.5} />
          <line
            x1={breakerX - 3} y1={y + 4} x2={breakerX + 3} y2={y - 4}
            stroke="#475569" strokeWidth={1.5} strokeLinecap="round"
          />
        </g>
      )}

      {/* Animated flow dots */}
      {showDots && (
        <g>
          {[0, 1, 2].map((i) => (
            <circle
              key={i}
              cx={dotOriginX}
              cy={y}
              r={LAYOUT.dotRadius}
              fill={color}
              className="sld-dot-flow"
              style={{
                animationDelay: `${i * 0.6}s`,
                ['--flow-distance' as string]: `${dotTravelPx}px`,
              }}
            />
          ))}
        </g>
      )}
    </g>
  );
}

export const BranchLine = memo(BranchLineInner);
